import { ArrowUpRightFromSquare, Banner, BellIcon, Button, TruckFastIcon } from '@quickmd-llc/design-system'
import type { ReactElement } from 'react'
import { DISMISS_ACTION_ID } from 'lib/iterable/constants'
import {
  announcementIconOf,
  customActionIdOf,
  labelledButtonsOf,
  type LabelledButton,
  opensInNewTab,
  targetUrlOf,
  type AnnouncementIcon,
  type RenderableMessage,
} from 'lib/iterable/types'

type Props = {
  message: RenderableMessage
  onDismiss: (buttonIdentifier: string) => void
  onAction: (buttonIdentifier: string, targetUrl: string) => void
}

/** The fallback label when a campaign ships with no buttons at all. */
const FALLBACK_DISMISS_LABEL = 'Got it'

const ICONS: Record<AnnouncementIcon, ReactElement> = {
  bell: <BellIcon iconSize='xl' />,
  'truck-fast': <TruckFastIcon iconSize='xl' />,
}

/**
 * One Iterable embedded message, drawn as the design system's `announcement` Banner
 * (Figma 13420:62841).
 *
 * Everything below `metadata` is authored in Iterable, so this renders only what a given message
 * actually has: no title, no body, no buttons and a label-less button are all shapes a published
 * campaign can take.
 *
 * `body` goes in as **text**, never markup. Iterable has no HTML field on an embedded message, so
 * there is nothing to interpret, and interpreting it anyway would turn the campaign editor into a
 * script-injection surface pointed at clinicians.
 */
export function AnnouncementBanner({ message, onDismiss, onAction }: Props) {
  const buttons = labelledButtonsOf(message)

  // The design has no close icon: a button *is* the dismissal. So a campaign that ships no
  // dismiss button pins its announcement to every clinician's dashboard until someone notices and
  // pauses the campaign in Iterable. That holds whether the campaign shipped no buttons at all or
  // only an Open URL link, so the fallback keys off "is there a way out of this" rather than off
  // the button count. Supplying one unasked-for button is cheaper than trusting every campaign to
  // be authored correctly, by people who publish without a release.
  const isDismissButton = (button: LabelledButton) => customActionIdOf(button.action) === DISMISS_ACTION_ID
  const hasDismiss = buttons.some(isDismissButton)

  // Iterable buttons carry no style, so priority is decided here. Beside a CTA, Dismiss is the
  // lesser action and Figma draws it Tertiary (no fill); with no CTA to defer to it is the banner's
  // main button. "Beside a CTA" means a drawn button that is not a dismiss: not the raw button
  // count, which a second dismiss or a label-less button (dropped above) would inflate.
  const hasCta = buttons.some((button) => !isDismissButton(button))
  // The supplied fallback goes first: Figma draws the tertiary dismiss to the left of the CTA. A
  // campaign's own buttons keep the order it authored them in.
  const actions = [
    ...(hasDismiss ?
      []
    : [
        <Button key='fallback-dismiss' priority={hasCta ? 'tertiary' : 'primary'} onClick={() => onDismiss('')}>
          {FALLBACK_DISMISS_LABEL}
        </Button>,
      ]),
    ...buttons.map((button) => {
      const isDismiss = isDismissButton(button)
      return (
        <Button
          key={button.id}
          priority={isDismiss && hasCta ? 'tertiary' : 'primary'}
          onClick={() => {
            if (isDismiss) {
              onDismiss(button.id)
              return
            }
            onAction(button.id, targetUrlOf(button.action))
          }}>
          {button.title}
          {/* A link out of the app says so before it is clicked: the icon for sighted users, the
              sr-only text for everyone else, since the icon itself is decorative. */}
          {opensInNewTab(button.action) && (
            <>
              <ArrowUpRightFromSquare />
              <span className='sr-only'>{' (opens in a new tab)'}</span>
            </>
          )}
        </Button>
      )
    }),
  ]

  const title = message.elements.title?.trim()
  const description = message.elements.body?.trim()
  const common = { variant: 'announcement', icon: ICONS[announcementIconOf(message)], actions } as const

  // `Banner` requires at least one of title/description, and a message can legitimately have
  // either. Branching satisfies that union without an assertion; `isRenderable` has already
  // guaranteed one of the two, so the final return is unreachable in practice.
  if (title) return <Banner {...common} title={title} {...(description ? { description } : {})} />
  if (description) return <Banner {...common} description={description} />
  return null
}
